/**
 * GEOMETRÍA — el sustrato común de todos los levantamientos.
 * =============================================================================
 *
 * Extraído de `riego.js` el 2026-08-14, sin cambiar una sola fórmula: es el paso 1 del
 * `Plan_Levantamiento_Guiado.md`, y se hizo primero justamente porque es código ya
 * probado. Las comprobaciones de `verificar-riego.mjs` lo cubren desde antes de moverlo,
 * así que el refactor se valida solo.
 *
 * **Por qué vive aparte:** `Simuladores_del_Cotizador.md` §4 lo dice sin rodeos — no son
 * varios simuladores, es uno. Riego, cámaras, seguridad, iluminación, Wi-Fi y eléctrico
 * comparten estas mismas cuatro operaciones:
 *
 *   · medir un recinto            → area, perimetro
 *   · saber si algo cae dentro    → dentro
 *   · encuadrar lo dibujado       → caja
 *   · llevar tubo o cable a todo  → arbolMinimo
 *
 * Lo que cambia entre rubros es la forma de la cobertura y de dónde sale la
 * canalización. La matemática de abajo no cambia.
 *
 * TODO LO DE ESTE ARCHIVO ES EXACTO Y VERIFICABLE. No hay un solo valor provisional:
 * son fórmulas de geometría elemental, comprobables contra un caso hecho a mano. Lo
 * provisional vive en los catálogos de cada rubro, nunca aquí.
 * ============================================================================= */

export const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y)

/**
 * Distancia Manhattan: la que recorre una zanja o una canalización, que va en ángulo
 * recto y no en diagonal. Rutear con distancia en línea recta y luego dibujar en
 * ángulo recto subestima los metros, y los metros son dinero.
 */
export const distOrtogonal = (a, b) => Math.abs(a.x - b.x) + Math.abs(a.y - b.y)

/** Área de un polígono por la fórmula del cordón (shoelace). Exacta. */
export function area (pts) {
  let a = 0
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    a += (pts[j].x + pts[i].x) * (pts[j].y - pts[i].y)
  }
  return Math.abs(a / 2)
}

/** Perímetro del polígono. */
export function perimetro (pts) {
  let p = 0
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) p += dist(pts[j], pts[i])
  return p
}

/** Punto dentro de polígono, por cruce de rayos. Exacto, y funciona con cóncavos. */
export function dentro (p, pts) {
  let d = false
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const yi = pts[i].y, yj = pts[j].y, xi = pts[i].x, xj = pts[j].x
    if ((yi > p.y) !== (yj > p.y) && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi) d = !d
  }
  return d
}

/** Rectángulo que contiene a los puntos. Sirve para encuadrar la vista. */
export function caja (pts) {
  const xs = pts.map(p => p.x), ys = pts.map(p => p.y)
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) }
}

/** Los lados de un polígono, con su medida y su punto medio. Para acotar el dibujo. */
export function lados (pts, cerrado = true) {
  const out = []
  const n = pts.length
  const hasta = cerrado ? n : n - 1
  for (let i = 0; i < hasta; i++) {
    const a = pts[i], b = pts[(i + 1) % n]
    out.push({ i, a, b, largo: dist(a, b), medio: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } })
  }
  return out
}

/**
 * Árbol de recorrido mínimo desde un origen (Prim), con distancia ortogonal.
 *
 * Es LA función compartida por todos los rubros que llevan algo desde un punto a
 * muchos: tubo desde la toma de agua, cable desde el centro de carga, UTP desde el
 * grabador, Cat6A desde el rack. Cambia el origen y el nombre de las piezas; el árbol
 * es el mismo.
 *
 * Devuelve los tramos ya quebrados en ele —primero en x, luego en y—, la longitud
 * total, y las piezas que se derivan de la FORMA del árbol:
 *   · un CODO por cada quiebre de noventa grados,
 *   · una TE por cada nodo del que salen dos o más ramas.
 */
export function arbolMinimo (origen, destinos) {
  if (!destinos.length) return { tramos: [], metros: 0, codos: 0, tes: 0, aristas: [] }

  const nodos = [{ ...origen, esOrigen: true }, ...destinos]
  const enArbol = [0]
  const fuera = nodos.map((_, i) => i).slice(1)
  const aristas = []

  while (fuera.length) {
    let mejorA = -1, mejorB = -1, mejorD = Infinity
    for (const a of enArbol) {
      for (const b of fuera) {
        const d = distOrtogonal(nodos[a], nodos[b])
        if (d < mejorD) { mejorD = d; mejorA = a; mejorB = b }
      }
    }
    aristas.push({ de: mejorA, a: mejorB, largo: mejorD })
    enArbol.push(mejorB)
    fuera.splice(fuera.indexOf(mejorB), 1)
  }

  const tramos = []
  let codos = 0
  for (const e of aristas) {
    const p = nodos[e.de], q = nodos[e.a]
    const codo = { x: q.x, y: p.y }
    const dx = Math.abs(q.x - p.x), dy = Math.abs(q.y - p.y)
    if (dx > 0.01) tramos.push({ a: p, b: codo })
    if (dy > 0.01) tramos.push({ a: codo, b: q })
    if (dx > 0.01 && dy > 0.01) codos++     // solo hay quiebre si el tramo tiene ambas
  }

  const salidas = {}
  for (const e of aristas) salidas[e.de] = (salidas[e.de] || 0) + 1
  const tes = Object.values(salidas).filter(n => n >= 2).length

  return { tramos, metros: aristas.reduce((s, e) => s + e.largo, 0), codos, tes, aristas }
}

/**
 * ¿Este punto cae dentro del alcance de un elemento?
 *
 * Sirve para las dos formas de cobertura que comparten cinco rubros: el CÍRCULO
 * —aspersor, luminaria— y el SECTOR —cámara, sensor de movimiento—. El Wi-Fi es el
 * único que necesita modelo físico y no pasa por aquí.
 *
 * @param e  { x, y, radio, arco?, orientacion? } — arco en grados, 360 = círculo
 */
export function alcanza (e, p) {
  const d = Math.hypot(e.x - p.x, e.y - p.y)
  if (d > e.radio) return false
  const arco = e.arco ?? 360
  if (arco >= 360) return true
  const ang = ((Math.atan2(p.y - e.y, p.x - e.x) * 180) / Math.PI + 360) % 360
  const ini = ((e.orientacion ?? 0) + 360) % 360
  return ((ang - ini + 360) % 360) <= arco
}

/**
 * Cobertura muestreada: qué proporción del recinto queda alcanzada.
 *
 * El DIBUJO de los huecos lo hace la interfaz con una máscara SVG, que es exacta y no
 * tiene resolución. Este muestreo existe para el NÚMERO —qué porcentaje queda fuera—,
 * porque un porcentaje hay que contarlo. `paso` en metros.
 */
export function cobertura (poligono, elementos, paso = 0.25) {
  if (poligono.length < 3) return { cubierto: 0, seco: 0, porcentaje: 0, huecos: [] }
  const b = caja(poligono)
  let dentroTotal = 0, cubiertos = 0
  const huecos = []
  for (let y = b.y0; y <= b.y1; y += paso) {
    for (let x = b.x0; x <= b.x1; x += paso) {
      const p = { x, y }
      if (!dentro(p, poligono)) continue
      dentroTotal++
      if (elementos.some(e => alcanza(e, p))) cubiertos++
      else huecos.push(p)
    }
  }
  const areaPunto = paso * paso
  return {
    cubierto: cubiertos * areaPunto,
    seco: (dentroTotal - cubiertos) * areaPunto,
    porcentaje: dentroTotal ? (cubiertos / dentroTotal) * 100 : 0,
    huecos
  }
}
